import { z } from "zod";

/** The four downloadable asset formats the generator targets. */
export const SOCIAL_FORMATS = [
  "ig_carousel",
  "ig_story",
  "ig_single",
  "pinterest_pin",
] as const;
export type SocialFormat = (typeof SOCIAL_FORMATS)[number];
export const socialFormatSchema = z.enum(SOCIAL_FORMATS);

/** Board layout style — mirrors the web carousel templates. `higgsfield` renders
 * full-bleed AI artwork (generated with Higgsfield) behind cinematic type.
 * `pewter` is the deep-blue centre-stage post: the site's own activity card,
 * in its pewter frame, on a lit navy canvas with the domain as the only CTA. */
export const SOCIAL_TEMPLATES = ["activity", "restaurant", "higgsfield", "pewter"] as const;
export type SocialTemplate = (typeof SOCIAL_TEMPLATES)[number];

/** One activity rendered on a slide/canvas. `activityId` lets us refresh images later. */
export const boardItemSchema = z.object({
  activityId: z.string(),
  title: z.string(),
  /** Category, segment or cuisine — shown as the chip. */
  label: z.string(),
  /** City badge on the card. */
  city: z.string(),
  /** Full image URL. */
  hero: z.string(),
  /** MAD; omit for explore items with no price. */
  price: z.number().nullable().optional(),
  /** Restaurant template: one-line editorial hook. */
  hook: z.string().optional(),
  /** Restaurant template: short fact pills. */
  notes: z.array(z.string()).optional(),
});
export type BoardItem = z.infer<typeof boardItemSchema>;

export const boardSchema = z.object({
  template: z.enum(SOCIAL_TEMPLATES).default("activity"),
  topLabel: z.string(),
  kicker: z.string(),
  coverLead: z.string(),
  coverAccent: z.string(),
  subtitle: z.string(),
  domain: z.string().default("majourneys.com"),
  /** Higgsfield template: closing CTA line on item slides, e.g. "Reserve now · pay on arrival". */
  ctaLine: z.string().optional(),
  items: z.array(boardItemSchema).min(1).max(10),
});
export type Board = z.infer<typeof boardSchema>;

export const socialCopySchema = z.object({
  caption: z.string(),
  hashtags: z.array(z.string()).default([]),
  pinterestTitle: z.string(),
  pinterestDescription: z.string(),
});
export type SocialCopy = z.infer<typeof socialCopySchema>;

export const SOCIAL_STATUSES = ["draft", "ready", "archived"] as const;
export type SocialStatus = (typeof SOCIAL_STATUSES)[number];

/** Persisted draft as returned by the API. */
export const socialPostDtoSchema = z.object({
  id: z.string(),
  title: z.string(),
  brief: z.string(),
  formats: z.array(socialFormatSchema),
  template: z.enum(SOCIAL_TEMPLATES),
  board: boardSchema,
  copy: socialCopySchema,
  status: z.enum(SOCIAL_STATUSES),
  model: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SocialPostDTO = z.infer<typeof socialPostDtoSchema>;

/** POST /social/generate request body. */
export const generateSocialInputSchema = z.object({
  brief: z.string().min(3).max(500),
  formats: z.array(socialFormatSchema).min(1),
});
export type GenerateSocialInput = z.infer<typeof generateSocialInputSchema>;

/** PATCH /social/:id request body — partial edits to a draft. */
export const updateSocialInputSchema = z.object({
  title: z.string().optional(),
  formats: z.array(socialFormatSchema).optional(),
  template: z.enum(SOCIAL_TEMPLATES).optional(),
  board: boardSchema.optional(),
  copy: socialCopySchema.optional(),
  status: z.enum(SOCIAL_STATUSES).optional(),
});
export type UpdateSocialInput = z.infer<typeof updateSocialInputSchema>;

/** The shape Claude must return from the generation prompt (pre-image-assembly). */
export const generatedDraftSchema = z.object({
  title: z.string(),
  template: z.enum(SOCIAL_TEMPLATES),
  /** Ordered subset of candidate activity slugs Claude chose. */
  slugs: z.array(z.string()).min(1).max(10),
  cover: z.object({
    topLabel: z.string(),
    kicker: z.string(),
    coverLead: z.string(),
    coverAccent: z.string(),
    subtitle: z.string(),
  }),
  /** Per-slug editorial, keyed by slug. */
  items: z.array(
    z.object({
      slug: z.string(),
      hook: z.string().optional(),
      notes: z.array(z.string()).optional(),
    }),
  ),
  copy: socialCopySchema,
});
export type GeneratedDraft = z.infer<typeof generatedDraftSchema>;
